import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import axios from "axios";
import { Clock, Search } from "lucide-react";
import SEOHead, { websiteSchema } from "../components/SEOHead";
import { API_BASE_URL, BACKEND_URL } from "../lib/site";

const API = API_BASE_URL;
const PLACEHOLDER_IMG = "data:image/svg+xml,%3Csvg xmlns='http://www.w3.org/2000/svg' width='400' height='250' viewBox='0 0 400 250'%3E%3Crect width='400' height='250' fill='%23F3F4F6'/%3E%3Ctext x='50%25' y='50%25' text-anchor='middle' dy='.3em' fill='%239CA3AF' font-family='sans-serif' font-size='14'%3ENo image%3C/text%3E%3C/svg%3E";
const isComingSoonArticle = (article) =>
  !article.content || (article.tags || []).some((tag) => tag.toLowerCase() === "coming soon");
const sortComingSoonLast = (items) =>
  [...items].sort((a, b) => Number(isComingSoonArticle(a)) - Number(isComingSoonArticle(b)));

export default function ArticlesPage() {
  const [articles, setArticles] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [search, setSearch] = useState("");
  const [activeTag, setActiveTag] = useState("All");
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();

  useEffect(() => {
    axios.get(`${API}/articles`)
      .then((r) => { setArticles(sortComingSoonLast(r.data)); })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => {
    setActiveTag(searchParams.get("cat") || "All");
  }, [searchParams]);

  const CATEGORIES = ["All", "FMCG", "D2C", "Marketplace", "Consumer Tech"];

  const handleTagChange = (tag) => {
    setActiveTag(tag);

    const nextParams = new URLSearchParams(searchParams);
    if (tag === "All") {
      nextParams.delete("cat");
    } else {
      nextParams.set("cat", tag);
    }

    setSearchParams(nextParams, { replace: true });
  };

  useEffect(() => {
    let res = articles;
    if (activeTag !== "All") res = res.filter((a) => (a.tags || []).some((t) => t.toLowerCase().includes(activeTag.toLowerCase())));
    if (search) res = res.filter((a) => a.title.toLowerCase().includes(search.toLowerCase()) || a.excerpt.toLowerCase().includes(search.toLowerCase()));
    setFiltered(sortComingSoonLast(res));
  }, [search, activeTag, articles]);

  return (
    <div className="pt-16 page-enter">
      <SEOHead
        title="Writing — Growth & Marketing Insights"
        description="What actually drives growth — beyond dashboards, playbooks, and paid media. From operating inside Amazon, Nestle, Zepto, Dabur and Emami."
        url={`${BACKEND_URL}/writing`}
        schema={websiteSchema}
      />
      {/* Header */}
      <div className="border-b border-[#E5E7EB] bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12 sm:py-16">
          <p className="text-[11px] tracking-[0.2em] uppercase font-bold text-[#002FA7] mb-3">Knowledge</p>
          <h1 className="font-['Outfit'] text-4xl sm:text-5xl lg:text-6xl font-light tracking-tight text-[#0A0A0A] mb-4">
            Writing
          </h1>
          <p className="text-[#4B5563] max-w-lg text-lg">
            What actually drives growth — beyond dashboards, playbooks, and paid media.
            <br className="hidden sm:block" />
            <span className="sm:hidden"> </span>
            From operating inside Amazon, Nestle, Zepto, Dabur and Emami.
          </p>
        </div>
      </div>

      {/* Filters */}
      <div className="border-b border-[#E5E7EB] bg-[#F9FAFB] sticky top-16 z-20">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-4 flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
          <div className="flex gap-2 flex-wrap">
            {CATEGORIES.map((tag) => (
              <button
                key={tag}
                onClick={() => handleTagChange(tag)}
                data-testid={`tag-filter-${tag}`}
                className={`text-xs px-3 py-1.5 font-medium transition-colors ${
                  activeTag === tag
                    ? "bg-[#002FA7] text-white"
                    : "border border-[#E5E7EB] text-[#4B5563] hover:border-[#002FA7] hover:text-[#002FA7] bg-white"
                }`}
              >
                {tag}
              </button>
            ))}
          </div>
          <div className="relative w-full sm:w-auto">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#9CA3AF]" />
            <input
              type="text"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search..."
              className="pl-8 pr-4 py-2 text-sm border border-[#E5E7EB] focus:outline-none focus:border-[#002FA7] bg-white w-full sm:w-48"
              data-testid="article-search"
            />
          </div>
        </div>
      </div>

      {/* Articles */}
      <div className="max-w-7xl mx-auto px-6 lg:px-12 py-12">
        {loading ? (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[1,2,3].map((i) => <div key={i} className="border border-[#E5E7EB] h-72 bg-[#F9FAFB] animate-pulse" />)}
          </div>
        ) : filtered.length === 0 ? (
          <div className="text-center py-20 text-[#9CA3AF]">No articles found.</div>
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6" data-testid="articles-grid">
            {filtered.map((article) => {
              const isComingSoon = isComingSoonArticle(article);
              const cardContent = (
                <>
                  <div className="aspect-video overflow-hidden bg-[#F3F4F6]">
                    {article.cover_image ? (
                      <img
                        src={article.cover_image}
                        alt={article.title}
                        className={`w-full h-full object-cover transition-transform duration-500 ${isComingSoon ? "" : "group-hover:scale-105"}`}
                        onError={(e) => { e.target.src = PLACEHOLDER_IMG; }}
                      />
                    ) : (
                      <div className="w-full h-full px-5 py-4 bg-[linear-gradient(135deg,#f8f4ed_0%,#ffffff_48%,#eef3fb_100%)] flex flex-col justify-between">
                        <span className="text-[10px] tracking-[0.18em] uppercase font-bold text-[#002FA7]">
                          Article
                        </span>
                        <p className="font-serif text-[1.7rem] leading-[1.18] text-[#111827] pr-3">
                          {article.title}
                        </p>
                        <div className="h-px bg-[#D6D3D1]" />
                      </div>
                    )}
                  </div>
                  <div className="p-5">
                    <div className="flex gap-2 mb-3 flex-wrap">
                      {(article.tags || []).filter((tag) => tag.toLowerCase() !== "coming soon").slice(0, 2).map((tag) => (
                        <span key={tag} className="text-[10px] tracking-[0.12em] uppercase font-bold text-[#002FA7] bg-blue-50 px-2 py-0.5">
                          {tag}
                        </span>
                      ))}
                    </div>
                    <h2 className={`font-['Outfit'] font-semibold text-[#0A0A0A] text-lg leading-tight mb-2 transition-colors ${isComingSoon ? "" : "group-hover:text-[#002FA7]"}`}>
                      {article.title}
                    </h2>

                    <div className="relative">
                      <div
                        className={isComingSoon ? "select-none" : ""}
                        style={isComingSoon ? { filter: "blur(3px)", opacity: 0.5 } : undefined}
                      >
                        <p className="text-sm text-[#6B7280] leading-relaxed line-clamp-3 mb-4">{article.excerpt}</p>
                        <div className="flex items-center gap-4 text-xs text-[#9CA3AF] pt-3 border-t border-[#F3F4F6]">
                          <span className="flex items-center gap-1"><Clock size={11} /> {article.read_time || 8} min</span>
                          {article.views > 0 && <span>{article.views} views</span>}
                        </div>
                      </div>

                      {isComingSoon && (
                        <div className="absolute inset-0 flex items-center justify-center bg-white/55">
                          <span className="bg-[#002FA7] text-white text-[11px] tracking-[0.18em] uppercase font-bold px-4 py-2">
                            Coming Next
                          </span>
                        </div>
                      )}
                    </div>
                  </div>
                </>
              );

              return isComingSoon ? (
                <article
                  key={article.id}
                  className="group block border border-[#E5E7EB] cursor-default bg-white"
                  data-testid={`article-${article.slug}`}
                >
                  {cardContent}
                </article>
              ) : (
                <Link
                  key={article.id}
                  to={`/writing/${article.slug}`}
                  className="group block border border-[#E5E7EB] card-hover"
                  data-testid={`article-${article.slug}`}
                >
                  {cardContent}
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
